import { Map } from 'immutable'
import debounce from 'lodash/debounce'
import { node, number, func, any, object } from 'prop-types'
import { Component, Children } from 'react'
import ResizeDetector from 'react-resize-detector'
import VirtualList from 'react-tiny-virtual-list-oss'
import { v4 as uuidv4 } from 'uuid'
import Loader from '../Loader'
import { kuiperClassNames } from '../utils'

import './styles.scss'

export const Group = () => null

Group.defaultProps = {
  isInfiniteGrouping: true,
}

const LOOKAHEAD_PIXELS = 5

const loader = () => (
  <div key={uuidv4()} style={{ height: 50 }}>
    <Loader theme="white" />
  </div>
)

const flattenList = (nested) =>
  Children.toArray(nested).reduce((outputList, topLevelItem) => {
    const { isInfiniteGrouping, collapsed, headerRow, children } =
      topLevelItem.props
    if (isInfiniteGrouping) {
      const header = headerRow(collapsed)
      if (collapsed) {
        return [...outputList, header]
      }
      return [...outputList, header, ...flattenList(children)]
    }
    return [...outputList, topLevelItem]
  }, [])

const lastHeader = (list, topItem, headerClass) => {
  for (let i = topItem; i > -1; i--) {
    if (list[i].type === headerClass) {
      return list[i]
    }
  }
  return null
}

export default class InfiniteList extends Component {
  static propTypes = {
    children: node,
    fetchMoreItems: func,
    lastLoadedPageSize: number,
    lazyLoading: object,
    marginBottom: number,
    rowHeight: number,
    scrollToIndex: number,
    stickyHeaders: any,
    style: object,
  }

  constructor(props) {
    super(props)
    this.state = {
      height: 0,
      width: 0,
      itemHeights: Map(),
      currentHeader: null,
      currentPageLoaded: 0,
      isLoadingData: false,
    }
    this.list = flattenList(props.children)
    this.debouncedFetchMoreItems = debounce(this.fetchMoreItems, 100)

    this.onResize = this.onResize.bind(this)
    this.componentDidMount = this.componentDidMount.bind(this)
    this.componentWillUnmount = this.componentWillUnmount.bind(this)
    this.trackList = this.trackList.bind(this)
    this.getHeight = this.getHeight.bind(this)
    this.rowRenderer = this.rowRenderer.bind(this)
    this.onScroll = this.onScroll.bind(this)
  }

  componentDidMount() {
    this.mounted = true
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.children !== this.props.children) {
      this.list = flattenList(nextProps.children)
    }
    if (this.props.lazyLoading) {
      if (
        this.state.isLoadingData &&
        nextProps.children.length === this.props.children.length &&
        nextProps.lastLoadedPageSize === this.props.lazyLoading.pageSize
      ) {
        this.list.push(loader())
      } else {
        this.setState({ isLoadingData: false })
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.itemHeights !== nextState.itemHeights ||
      nextProps.children !== this.props.children ||
      this.state.currentHeader !== nextState.currentHeader ||
      this.state.isLoadingData !== nextState.isLoadingData
    )
  }

  componentWillUnmount() {
    this.mounted = false
  }

  fetchMoreItems() {
    const { pageSize } = this.props.lazyLoading
    const { lastLoadedPageSize } = this.props
    if (pageSize <= lastLoadedPageSize) {
      this.list.push(loader())
      this.setState(
        (prevState) => {
          if (!prevState.isLoadingData) {
            return {
              currentPageLoaded: prevState.currentPageLoaded + pageSize,
              isLoadingData: true,
            }
          }
          return {}
        },
        this.props.lazyLoading.fetchMoreItems({
          page: this.state.currentPageLoaded + pageSize,
          pageSize,
        })
      )
    } else {
      this.setState({ isLoadingData: false })
    }
  }

  saveRef(index) {
    return (ref) => {
      if (ref) {
        this.setState((prevState) => {
          const thisHeight = ref.children[0]
            ? ref.children[0].clientHeight
            : 120
          if (prevState.itemHeights.get(index) === thisHeight) {
            return {}
          }
          return { itemHeights: prevState.itemHeights.set(index, thisHeight) }
        })
      }
    }
  }

  getHeight(index) {
    const { rowHeight = 40, marginBottom = 2 } = this.props
    return this.state.itemHeights.get(index, rowHeight) + marginBottom
  }

  rowRenderer({ index, style }) {
    const { list } = this
    return (
      <div key={index} style={style} ref={this.saveRef(index)}>
        {list[index]}
      </div>
    )
  }

  get renderList() {
    const { list, trackList, getHeight, onScroll, rowRenderer } = this
    const { width, height } = this.state
    const { rowHeight = 40, scrollToIndex, style } = this.props

    return (
      <VirtualList
        style={style}
        ref={trackList}
        onScroll={onScroll}
        width={width || '100%'}
        height={height}
        scrollToIndex={scrollToIndex}
        scrollToAlignment="auto"
        estimatedItemSize={rowHeight}
        itemCount={list.length}
        itemSize={getHeight.bind(this)}
        renderItem={rowRenderer.bind(this)}
      />
    )
  }

  trackList(listComponent) {
    this.listComponent = listComponent
  }

  onScroll(scrollTop) {
    if (this.listComponent && scrollTop !== this.state.scrollTop) {
      const { rowHeight = 40 } = this.props
      const visibleItems =
        this.listComponent.sizeAndPositionManager.getVisibleRange({
          containerSize: this.listComponent.props.height,
          offset: 1 * (scrollTop - rowHeight),
          overscanCount: 0,
        })

      const lookAheadCount = 1

      const fetchCondition =
        this.props.lazyLoading &&
        this.props.lazyLoading.shouldLazyLoad &&
        visibleItems.stop === this.list.length - lookAheadCount - 1 &&
        scrollTop >= this.state.scrollTop - LOOKAHEAD_PIXELS &&
        !this.state.isLoadingData

      if (fetchCondition) {
        this.debouncedFetchMoreItems()
      }

      if (this.props.stickyHeaders) {
        this.setState({
          currentHeader: lastHeader(
            this.list,
            visibleItems.start,
            this.props.stickyHeaders
          ),
        })
      }
      this.setState({
        scrollTop,
      })
    }
  }

  get header() {
    if (this.props.stickyHeaders && this.state.currentHeader) {
      return (
        <div className={kuiperClassNames('headerBar')}>
          {this.state.currentHeader}
        </div>
      )
    }
    return null
  }

  onResize(width, height) {
    if (
      this.mounted &&
      (width !== this.state.width || height !== this.state.height)
    ) {
      this.setState(
        {
          width,
          height,
        },
        (this.listComponent && this.listComponent.forceUpdate) || (() => {})
      )
    }
  }

  render() {
    return (
      <div className={kuiperClassNames('infinite-list', kuiperClassNames)}>
        {this.renderList}
        {this.header}
        <ResizeDetector
          handleWidth={true}
          handleHeight={true}
          onResize={this.onResize}
        />
      </div>
    )
  }
}
